import React from 'react'
import { connect } from 'react-redux'
import { Location, Navigate, NavigateFunction, Route, useLocation, useNavigate } from 'react-router-dom'

import { Map } from 'immutable'
import * as _ from 'lodash-es'
import queryString from 'query-string'
import { bindActionCreators } from 'redux'

import NavButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/navButton/NavButton'
import BootstrapContainer from '@lyrahealth-inc/ui-core/lib/molecules/layouts/bootstrapContainer/BootstrapContainer'

import styles from './providerDetails.module.scss'
import DisplayProviderRates from './rates/DisplayProviderRates'
import EditProviderRates from './rates/EditProviderRates'
import TabFilters from '../../../common/components/tabFilters/TabFilters'
import {
  PRACTICES_DETAILS_PROVIDERS,
  PROVIDERS,
  PROVIDERS_DETAILS_CALENDAR,
  PROVIDERS_DETAILS_EDIT_INFO,
  PROVIDERS_DETAILS_EDIT_RATES,
  PROVIDERS_DETAILS_INFO,
  PROVIDERS_DETAILS_RATES,
} from '../../../common/constants/routingConstants'
import { axiosAuthInstance } from '../../../common/http/axiosInstance'
import { RootState } from '../../../data/store'
import { SentryRoutes } from '../../../index'
import {
  getProviderDetailsData,
  getProviderDetailsDiffPricingEnabled,
  getProviderDetailsDiffPricingModifier,
} from '../../data/providerSelectors'
import * as providerDetailsActions from '../data/providerDetailsActions'
// pages
import DisplayProviderInfo from '../info/DisplayProviderInfo'
import EditProviderInfo from '../info/EditProviderInfo'

type ProviderDetailsProps = {
  navigate: NavigateFunction
  location: Location
  actions?: any
  providerDetails?: any // TODO: PropTypes.instanceOf(Map)
  diffPricingStatus?: boolean
  diffPricingInPersonModifier?: number
}

class ProviderDetails extends React.Component<ProviderDetailsProps> {
  componentDidMount() {
    const { navigate, location } = this.props
    const providerId =
      (location.state && location.state.provider_id) ||
      // Reading the `id` from the url is useful for routing from the `calendar` python page to this view.
      (queryString.parse(location.search) && queryString.parse(location.search).id)
    if (providerId) {
      // get provider by ID
      this.props.actions.getProvider(providerId)
    } else {
      // If we don't have an ID, punt them back to the providers list
      navigate(PROVIDERS.route, { replace: true })
    }
  }

  componentWillUnmount() {
    this.props.actions.clearProviderDetailsStore()
    if (typeof (axiosAuthInstance as any).cancelLastRequest === 'function') {
      ;(axiosAuthInstance as any).cancelLastRequest()
    }
  }

  _backToProviders = () => {
    const { navigate, location } = this.props
    if (location.state && location.state.practiceId) {
      return navigate(PRACTICES_DETAILS_PROVIDERS.route, {
        state: { practiceId: location.state.practiceId },
      })
    }

    navigate(PROVIDERS.route, { replace: true })
  }

  _handleTabClick = (tab: any) => {
    const providerId = this.props.providerDetails?.lyra_id
    switch (tab.toLowerCase()) {
      case 'rates':
        this.props.navigate(PROVIDERS_DETAILS_RATES.route, { replace: true })
        break
      case 'info':
        this.props.navigate(PROVIDERS_DETAILS_INFO.route, { replace: true })
        break
      case 'calendar':
        this.props.navigate(`${PROVIDERS_DETAILS_CALENDAR.route}/${providerId}`, { replace: true })
    }
  }

  _renderTabs = () => {
    let tab
    const pathname = this.props.location.pathname
    if (_.includes(pathname, 'info')) {
      tab = 'Info'
    } else if (_.includes(pathname, 'rates')) {
      tab = 'Rates'
    } else if (_.includes(pathname, 'calendar')) {
      tab = 'Calendar'
    }

    // @ts-expect-error TS(2322): Type '(tab: any) => void' is not assignable to typ... Remove this comment to see the full error message
    return <TabFilters activeTab={tab} tabList={['INFO', 'RATES', 'CALENDAR']} handleClick={this._handleTabClick} />
  }

  _submitRates = ($$values: any) => {
    return this.props.actions.editRates(this.props.providerDetails.lyra_id, $$values.toJS()).then(() => {
      this.props.navigate(PROVIDERS_DETAILS_RATES.route, { replace: true })
    })
  }

  render() {
    if (!this.props.providerDetails) {
      return false
    }

    const { providerDetails, diffPricingStatus, diffPricingInPersonModifier } = this.props
    const rates = { ...providerDetails.rates }
    rates.differential_pricing_enabled = diffPricingStatus
    rates.differential_pricing_in_person_modifier = diffPricingInPersonModifier

    return (
      <BootstrapContainer>
        <NavButton
          // @ts-expect-error TS(2322): Type '{ className: string; styleType: string; text... Remove this comment to see the full error message
          className={styles['back-button']}
          styleType='back'
          text='Back to Providers'
          onClick={this._backToProviders}
        />
        <div className={styles.overview}>
          <h2>
            {providerDetails.first_name} {providerDetails.last_name}
          </h2>
          <p>{providerDetails.email}</p>
        </div>
        {this._renderTabs()}
        <SentryRoutes>
          <Route
            path={PROVIDERS_DETAILS_EDIT_INFO.subroute}
            element={<EditProviderInfo initialValues={Map(providerDetails)} />}
          />
          <Route path={PROVIDERS_DETAILS_INFO.subroute} element={<DisplayProviderInfo data={providerDetails} />} />
          <Route
            path={PROVIDERS_DETAILS_EDIT_RATES.subroute}
            element={
              // @ts-expect-error TS(2322): Type '{ initialValues: any; _submitForm: ($$values... Remove this comment to see the full error message
              <EditProviderRates initialValues={Map(rates)} _submitForm={this._submitRates} />
            }
          />
          <Route path={PROVIDERS_DETAILS_RATES.subroute} element={<DisplayProviderRates $$data={Map(rates)} />} />
          <Route path='*' element={<Navigate to={PROVIDERS_DETAILS_INFO.route} />} />
        </SentryRoutes>
      </BootstrapContainer>
    )
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    providerDetails: getProviderDetailsData(state),
    diffPricingStatus: getProviderDetailsDiffPricingEnabled(state),
    diffPricingInPersonModifier: getProviderDetailsDiffPricingModifier(state),
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    actions: bindActionCreators({ ...providerDetailsActions }, dispatch),
  }
}

const ConnectedProviderDetails = connect(mapStateToProps, mapDispatchToProps)(ProviderDetails)

export default function ProviderDetailsWithRouter() {
  const navigate = useNavigate()
  const location = useLocation()
  return <ConnectedProviderDetails navigate={navigate} location={location} />
}
